import { GAME_FX } from '@utils/GameEffects';

export default class TextBox extends Phaser.GameObjects.Container {
  constructor(scene, x, y, config = {}) {
    super(scene, x, y);
    scene.add.existing(this);

    // Config
    this.character = config.character;
    this.characterPosition = config.characterPosition || {};
    this.origin = config.origin || { x: 0.5, y: 0.5 };

    this.paddingVertical = config.paddingVertical || 30;
    this.paddingHorizontal = config.paddingHorizontal || 22;
    this.textColor = config.textColor || '#ffffff';
    this.backgroundColor = config.backgroundColor || 0x000000;
    this.backgroundAlpha = config.backgroundAlpha || 0.65;
    this.borderColor = config.borderColor || 0x000000;
    this.borderWidth = config.borderWidth || 3.2;
    this.borderRadius = config.borderRadius || 12.8;
    this.animationDuration = config.animationDuration || 500;

    // Fixed-size pill mode. When set, background uses these dims instead of
    // sizing to the text. Use with `textOrigin` / `textOffset` to position the
    // text inside the fixed box (e.g. next to a character on the left edge).
    this.fixedWidth = config.fixedWidth;
    this.fixedHeight = config.fixedHeight;
    this.textOrigin = config.textOrigin || this.origin;
    this.textOffset = config.textOffset || null;
    this.fontSize = config.fontSize || '28.8px';
    this.fontFamily = config.fontFamily || 'Athletics-Bold';
    this.wordWrapWidth = config.wordWrapWidth || 1040;

    // Graphics object for the background
    this.background = scene.add.graphics();
    this.add(this.background);

    // Text
    this.textObj = scene.add
      .text(0, 0, '', {
        fontFamily: this.fontFamily,
        fontSize: this.fontSize,
        color: this.textColor,
        wordWrap: { width: this.wordWrapWidth },
        align: config.textAlign || (this.character ? 'left' : 'center'),
      })
      .setOrigin(this.textOrigin.x, this.textOrigin.y)
      .setAlpha(0);
    this.add(this.textObj);

    // Character
    if (this.character) {
      this.characterImage = scene.add
        .image(
          this.characterPosition.x ?? 0,
          this.characterPosition.y ?? -10,
          this.character,
        )
        .setOrigin(0.5)
        .setAlpha(0);

      this.add(this.characterImage);
    }

    // Start hidden
    this.setVisible(false);
    this.background.setAlpha(0);
    this.background.scaleX = 0; // fake scale tween
  }

  getBorderRadius() {
    const r = this.borderRadius;
    if (typeof r === 'number') return r;
    const lineCount = this.textObj.getWrappedText().length;
    if (lineCount === 2 && this.character && !this.fixedHeight) {
      return { tl: r.tl, tr: r.tr, bl: 50, br: r.br };
    }
    return { tl: r.tl, tr: r.tr, bl: r.bl, br: r.br };
  }

  drawBackground(width, height) {
    this.background.clear();

    // Compute top-left of the box based on origin
    const ox = this.origin.x * width;
    const oy = this.origin.y * height;

    const x = -ox;
    const y = -oy;

    const lineCount = this.textObj.getWrappedText().length;
    const radius = this.getBorderRadius();
    const yShift = lineCount === 2 && !this.fixedHeight ? 10 : 0;

    // Fill
    this.background.fillStyle(this.backgroundColor, this.backgroundAlpha);
    this.background.fillRoundedRect(x, y + yShift, width, height, radius);

    // Border
    this.background.lineStyle(this.borderWidth, this.borderColor, 1);
    this.background.strokeRoundedRect(x, y + yShift, width, height, radius);
  }

  resizeToFitText(text) {
    this.textObj.setText(text);

    const bounds = this.textObj.getBounds();

    const width = this.fixedWidth ?? bounds.width + this.paddingHorizontal * 2;
    const height = this.fixedHeight ?? bounds.height + this.paddingVertical * 2;

    let textObjX;
    let textObjY;
    if (this.textOffset) {
      textObjX = this.textOffset.x;
      textObjY = this.textOffset.y;
    } else {
      textObjX = this.paddingHorizontal - this.paddingHorizontal * (this.origin.x * 2);
      const lineCount = this.textObj.getWrappedText().length;
      textObjY = this.paddingVertical - this.paddingVertical * (this.origin.y * 2);
      if (lineCount === 2 && !this.fixedHeight) textObjY += 10;
    }

    this.textObj.setPosition(textObjX, textObjY);

    this.drawBackground(width, height);
  }

  processText(text) {
    if (typeof text !== 'string' || text.length < 5) return text;

    let result = '';
    let param = '';
    let started = false;
    let insideBracket = false;

    for (let i = 0; i < text.length; i++) {
      const current = text[i];
      const next = text[i + 1];

      // Detect start "{["
      if (!started && current === '{' && next === '[') {
        started = true;
        insideBracket = true;
        param = '';
        i++; // skip '['
        continue;
      }

      // Capture parameter inside [...]
      if (started && insideBracket) {
        if (current === ']') {
          insideBracket = false;
          continue;
        }
        param += current;
        continue;
      }

      // Detect end "}"
      if (started && current === '}') {
        const tabCount = Math.max(10, Math.round(Number(param) / 6));
        const replacement = '\t'.repeat(tabCount);

        result += replacement;
        started = false;
        continue;
      }

      // Normal characters
      if (!started) {
        result += current;
      }
    }

    return result;
  }

  showText(textToDisplay) {
    const text = this.processText(textToDisplay);

    if (this.visible) {
      GAME_FX.resize(this.background, { x: 0 }, this.animationDuration, {
        ease: 'Cubic.easeIn',
      });
      GAME_FX.fade(this.textObj, 0, this.animationDuration / 2, { ease: 'Cubic.easeIn' });

      this.scene.time.delayedCall(this.animationDuration, () => {
        this.resizeToFitText(text);

        // Reset visuals
        this.background.setAlpha(1);
        this.background.scaleX = 0;
        this.textObj.setAlpha(0);

        GAME_FX.resize(this.background, { x: 1 }, this.animationDuration, {
          ease: 'Cubic.easeOut',
        });
        GAME_FX.fade(this.textObj, 1, this.animationDuration, {
          ease: 'Cubic.easeOut',
          delay: 400,
        });
      });

      return;
    }

    this.setVisible(true);
    this.resizeToFitText(text);

    // Reset visuals
    this.background.setAlpha(1);
    this.background.scaleX = 0;
    this.textObj.setAlpha(0);
    this.characterImage?.setAlpha(0);

    if (this.characterImage) {
      GAME_FX.fade(this.characterImage, 1, 100, { ease: 'Cubic.easeOut' });
    }

    GAME_FX.resize(this.background, { x: 1 }, this.animationDuration, {
      ease: 'Cubic.easeOut',
    });
    GAME_FX.fade(this.textObj, 1, this.animationDuration, {
      ease: 'Cubic.easeOut',
      delay: 200,
    });
  }

  hideText() {
    GAME_FX.resize(this.background, { x: 0 }, this.animationDuration, {
      ease: 'Cubic.easeIn',
      onComplete: () => {
        if (this.characterImage) {
          GAME_FX.fade(this.characterImage, 0, this.animationDuration / 2, {
            ease: 'Cubic.easeOut',
            onComplete: () => this.setVisible(false),
          });
        } else {
          this.setVisible(false);
        }
      },
    });

    GAME_FX.fade(this.textObj, 0, this.animationDuration / 2, { ease: 'Cubic.easeIn' });
  }
}
